import { useMemo } from 'react';
import { Sparkles, Wrench, Bug } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { visibleReleaseNotes } from '@/data/releaseNotes';
import type { ReleaseNote } from '@/data/releaseNotes';
import { cn } from '@/lib/utils';

const TAG_STYLES: Record<NonNullable<ReleaseNote['tag']>, { className: string; icon: typeof Sparkles }> = {
  New: { className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', icon: Sparkles },
  Improved: { className: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300', icon: Wrench },
  Fix: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', icon: Bug },
};

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function ReleaseNotes() {
  const { hasRole } = useAuth();
  const { canManagePeople } = useOrganisation();
  const isAdmin = hasRole('admin') || hasRole('company_admin') || canManagePeople;
  const notes = useMemo(() => visibleReleaseNotes(isAdmin), [isAdmin]);

  return (
    <div className="mx-auto max-w-3xl animate-fade-in px-4 py-6 sm:p-8">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-card-foreground sm:text-4xl">What’s New</h1>
        <p className="text-lg text-muted-foreground">
          The latest improvements to the platform. {isAdmin ? 'You’re seeing all updates, including administrator and manager features.' : ''}
        </p>
      </div>

      <ol className="relative border-s border-border">
        {notes.map((note) => {
          const tag = note.tag ? TAG_STYLES[note.tag] : null;
          const TagIcon = tag?.icon;
          return (
            <li key={note.id} className="mb-10 ms-6">
              <span className="absolute -start-1.5 mt-2 size-3 rounded-full bg-primary ring-4 ring-background" />
              <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <time className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {formatDate(note.date)}
                  </time>
                  {tag && TagIcon && (
                    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', tag.className)}>
                      <TagIcon size={12} />
                      {note.tag}
                    </span>
                  )}
                  {isAdmin && note.audience === 'admin' && (
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      Administrators and managers
                    </span>
                  )}
                </div>
                <h2 className="mt-2 text-xl font-bold text-card-foreground">{note.title}</h2>
                <p className="mt-1 text-muted-foreground">{note.summary}</p>
                <ul className="mt-3 space-y-1.5">
                  {note.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-card-foreground">
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary/60" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ol>

      {notes.length === 0 && (
        <p className="text-muted-foreground">No release notes yet.</p>
      )}
    </div>
  );
}

export default ReleaseNotes;
